import React, { useEffect, useState, useCallback } from "react";
import {
  Box, Flex, Text,
  Heading,
  Button,
  Spinner,
} from "@chakra-ui/react";
import AgGridWrapper from "../../components/AgGrid/AgGridWrapper";
import { ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { saveAs } from "file-saver";

import apiClient from "Configuration/apiClient";
import API_CONFIG from "../../Configuration/apiConfig";
import { getCurrentDate, getFirstDayOfMonth, parseDate } from "../../Utils/dateUtils";

export default function RedFreshness() {
  const [rowData, setRowData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [filteredCount, setFilteredCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize] = useState(20);
  const [downloadLoading, setDownloadLoading] = useState(false);
  const [error, setError] = useState(null);

  const [fromDate, setFromDate] = useState(getFirstDayOfMonth);
  const [toDate, setToDate] = useState(getCurrentDate());

  const handleFromDateChange = (value) => {
    setFromDate(value);
    if (value > toDate) setToDate(value);
  };

  const handleToDateChange = (value) => {
    if (value < fromDate) return;
    setToDate(value);
  };

  const fetchData = useCallback(async () => {
    if (!fromDate || !toDate) return;
    setLoading(true);
    setError(null);
    setRowData([]);

    const from = parseDate(fromDate);
    const to = parseDate(toDate);

    const requestBody = {
      from: { date: from.day, month: from.month, year: from.year },
      to: { date: to.day, month: to.month, year: to.year },
    };

    try {
      const res = await apiClient.post(
        `${API_CONFIG.ENDPOINTS.RED_FRESHNESS.REPORT}`,
        requestBody
      );

      const list = Array.isArray(res.data) ? res.data : [res.data];
      const mapped = list.map((item) => {
    return {
      shopNo: item.SHOPNO || "",
      customer: item.CUSTOMER || "",
      outletName: item["OUTLET NAME"] || "",
      address: item.ADDRESS || "",
      region: item.REGION || "",
      subRegion: item["SUB REGION"] || "",
      outletType: item["OUTLET TYPE"] || "",
      class: item.CLASS || "",
      brand: item.BRAND || "",
      packaging: item.PACKAGING || "",
      sku: item.SKU || "",
      surveyDate: item["SURVEY DATE"] || "",
      mfd: item.MFD || "",
      expiryDate: item["EXPIRY DATE"] || "",
      age: item.AGE ?? "",
      sample: item.SAMPLE ?? "",
      qno: item.QNO || "",
      mdcCode: item.MDCcode || "",
      mdc: item.MDC || "",
      rsm: item.RSM || "",
      asm: item.ASM || "",
      salesman: item.SALESMAN || "",
      salesOff: item.SALESOFF || "",
    };
  });

      setRowData(mapped);
    } catch (err) {
      console.error("Failed to fetch red freshness data:", err);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  }, [fromDate, toDate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Grid callbacks ─── */
  const onGridReady = (params) => {
    setGridApi(params.api);
    setFilteredCount(params.api.getDisplayedRowCount());
  };

  const onFilterChanged = useCallback((params) => {
    if (params?.api) setFilteredCount(params.api.getDisplayedRowCount());
  }, []);

  const totalPages = Math.ceil(filteredCount / pageSize);

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage((p) => p - 1);
      gridApi?.paginationGoToPreviousPage();
    }
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage((p) => p + 1);
      gridApi?.paginationGoToNextPage();
    }
  };

  /* ─── Excel Download ─── */
  const handleDownloadExcel = async () => {
    setDownloadLoading(true);
    try {
      const exportData = [];
      if (gridApi) {
        gridApi.forEachNodeAfterFilter((node) => exportData.push(node.data));
      }
      const dataToExport = exportData.length > 0 ? exportData : rowData;

      const HEADERS = [
        "SHOPNO", "CUSTOMER", "OUTLET NAME", "ADDRESS", "REGION", "SUB REGION",
        "OUTLET TYPE", "CLASS", "BRAND", "PACKAGING", "SKU", "SURVEY DATE",
        "MFD","EXPIRY DATE", "AGE", "SAMPLE", "QNO", "MDCcode", "MDC", "RSM", "ASM","SALESMAN",
        "SALESOFF",
      ];

        const FIELDS = [
        "shopNo", "customer", "outletName", "address", "region", "subRegion",
        "outletType", "class", "brand", "packaging", "sku", "surveyDate",
        "mfd", "expiryDate", "age", "sample", "qno", "mdcCode", "mdc", "rsm", "asm",
        "salesman", "salesOff",
       ];
  

      /* Header style: red fill, white bold text, black border */
      const headerStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "C72127" } },
        font: { bold: true, color: { rgb: "FFFFFF" }, sz: 12 },
        alignment: { horizontal: "center", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      /* Data row style: white fill, dark text, black border */
      const dataStyle = {
        fill: { patternType: "solid", fgColor: { rgb: "FFFFFF" } },
        font: { color: { rgb: "2D3748" }, sz: 11 },
        alignment: { horizontal: "left", vertical: "center", wrapText: true },
        border: {
          top: { style: "thin", color: { rgb: "000000" } },
          bottom: { style: "thin", color: { rgb: "000000" } },
          left: { style: "thin", color: { rgb: "000000" } },
          right: { style: "thin", color: { rgb: "000000" } },
        },
      };

      /* Build worksheet as array-of-cell-objects */
      const wsData = [
        HEADERS.map((h) => ({ v: h, t: "s", s: headerStyle })),
        ...dataToExport.map((row) =>
          FIELDS.map((f) => ({
            v: row[f] !== undefined && row[f] !== null ? String(row[f]) : "",
            t: "s",
            s: dataStyle,
          }))
        ),
      ];

      const XLSX = await import("xlsx-js-style");
      const ws = XLSX.utils.aoa_to_sheet(wsData);
      ws["!cols"] = HEADERS.map(() => ({ wch: 20 }));
      ws["!rows"] = [{ hpt: 30 }, ...dataToExport.map(() => ({ hpt: 22 }))];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Red Freshness Report");

      const buf = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      saveAs(
        new Blob([buf], {
          type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        }),
        `RedFreshness_Report_${fromDate}_to_${toDate}.xlsx`
      );
    } catch (err) {
      console.error("Excel download failed:", err);
      alert("Failed to download Excel. Please try again.");
    } finally {
      setDownloadLoading(false);
    }
  };

  /* ─── Column definitions ─── */
  const columnDefs = [
    { headerName: "SHOPNO", field: "shopNo", minWidth: 120, flex: 1 },
    { headerName: "CUSTOMER", field: "customer", minWidth: 110, flex: 1 },
    { headerName: "OUTLET NAME", field: "outletName", minWidth: 200, flex: 1.5 },
    { headerName: "ADDRESS", field: "address", minWidth: 150, flex: 1.5 },
    { headerName: "REGION", field: "region", minWidth: 100, flex: 1 },
    { headerName: "SUB REGION", field: "subRegion", minWidth: 120, flex: 1 },
    { headerName: "OUTLET TYPE", field: "outletType", minWidth: 120, flex: 1 },
    { headerName: "CLASS", field: "class", minWidth: 100, flex: 1 },
    { headerName: "BRAND", field: "brand", minWidth: 130, flex: 1 },
    { headerName: "PACKAGING", field: "packaging", minWidth: 110, flex: 1 },
    { headerName: "SKU", field: "sku", minWidth: 90, flex: 0.8 },
    { headerName: "SURVEY DATE", field: "surveyDate", minWidth: 120, flex: 1 },
    { headerName: "MFD", field: "mfd", minWidth: 110, flex: 1 },
    {headerName:"EXPIRY DATE",field:"expiryDate",minWidth:110,flex:1},
    { headerName: "AGE", field: "age", minWidth: 80, flex: 0.7 },
    { headerName: "SAMPLE", field: "sample", minWidth: 90, flex: 0.8 },
    { headerName: "QNO", field: "qno", minWidth: 100, flex: 0.9 },
    { headerName: "MDCcode", field: "mdcCode", minWidth: 100, flex: 0.9 },
    { headerName: "MDC", field: "mdc", minWidth: 150, flex: 1.3 },
    { headerName: "RSM", field: "rsm", minWidth: 150, flex: 1.3 },
    { headerName: "ASM", field: "asm", minWidth: 150, flex: 1.3 },
    { headerName: "SALESMAN",    field: "salesman",   minWidth: 140, flex: 1.2 },
    { headerName: "SALESOFF", field: "salesOff", minWidth: 140, flex: 1.2 },
  ];

  /* ─── Render ─── */
  return (
    <Box
      minH="100vh"
      bg="gray.50"
      pt={{ base: "7vh", sm: "8vh", md: "9vh", lg: "10vh" }}
      px={{ base: 3, sm: 4, md: 6, lg: 8 }}
      pb={{ base: 4, sm: 5, md: 6 }}
    >
      <Box mb={{ base: 4, sm: 5, md: 6 }}>

        {/* ── Header + Date Pickers ── */}
        <Flex
          direction={{ base: "column", sm: "row" }}
          justify="space-between"
          align={{ base: "flex-start", sm: "center" }}
          mb={{ base: 3, sm: 4, md: 4 }}
          gap={{ base: 3, sm: 0 }}
        >
          <Heading
            as="h1"
            fontSize={{ base: "20px", sm: "25px", md: "30px", lg: "35px" }}
            fontWeight="bold"
            color="gray.800"
          >
            Red Freshness Report
          </Heading>

          <Flex
            gap={{ base: 2, sm: 3 }}
            direction={{ base: "column", sm: "row" }}
            w={{ base: "100%", sm: "auto" }}
            align={{ base: "stretch", sm: "flex-end" }}
          >
            {/* FROM Date */}
            <Box w={{ base: "100%", sm: "auto" }}>
              <Text
                fontSize={{ base: "xs", sm: "sm", md: "md" }}
                color="gray.800"
                fontWeight="bold"
                mb={1}
                mx={2}
              >
                From Date
              </Text>
              <Box
                as="input"
                type="date"
                value={fromDate}
                max={toDate}
                onChange={(e) => handleFromDateChange(e.target.value)}
                w={{ base: "100%", md: "180px" }}
                p={{ base: "5px 8px", sm: "7px 11px", md: "8px 12px" }}
                borderRadius="8px"
                border="1px solid"
                borderColor="gray.300"
                bg="white"
                fontSize={{ base: "13px", sm: "15px", md: "16px" }}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  outline: "none",
                  borderColor: "red.500",
                  boxShadow: "0 0 0 3px rgba(244, 0, 9, 0.3)",
                }}
              />
            </Box>

            {/* TO Date */}
            <Box w={{ base: "100%", sm: "auto" }}>
              <Text
                fontSize={{ base: "xs", sm: "sm", md: "md" }}
                fontWeight="bold"
                mb={1}
                mx={2}
                color="gray.800"
              >
                To Date
              </Text>
              <Box
                as="input"
                type="date"
                value={toDate}
                min={fromDate}
                onChange={(e) => handleToDateChange(e.target.value)}
                w={{ base: "100%", md: "180px" }}
                p={{ base: "5px 8px", sm: "7px 11px", md: "8px 12px" }}
                borderRadius="8px"
                border="1px solid"
                borderColor="gray.300"
                bg="white"
                fontSize={{ base: "13px", sm: "15px", md: "16px" }}
                cursor="pointer"
                transition="all 0.2s"
                _hover={{ borderColor: "gray.400" }}
                _focus={{
                  outline: "none",
                  borderColor: "red.500",
                  boxShadow: "0 0 0 3px rgba(244, 0, 9, 0.3)",
                }}
              />
            </Box>
          </Flex>
        </Flex>

        <Flex
          align="center"
          gap={3}
          direction={{ base: "column", sm: "row" }}
          mb={{ base: 3, md: 4 }}
        >
          <Flex
            align="center"
            gap={1.5}
            bg="gray.700"
            px={{ base: 3, md: 4 }}
            py={{ base: 1, md: 1.5 }}
            borderRadius="full"
            boxShadow="0 2px 4px rgba(0, 0, 0, 0.2)"
          >
            <Text fontSize={{ base: "xs", sm: "sm", md: "md" }} fontWeight="bold" color="white">
              {filteredCount < rowData.length ? "Filtered:" : "Total:"}
            </Text>
            <Text color="white" fontSize={{ base: "sm", sm: "md", md: "lg" }} fontWeight="bold">
              {filteredCount} Records
            </Text>
          </Flex>

          <Button
            colorScheme="green"
            size={{ base: "sm", md: "md" }}
            onClick={handleDownloadExcel}
            isLoading={downloadLoading}
            loadingText="Downloading..."
            spinner={<Spinner size="sm" color="white" />}
            rightIcon={
              !downloadLoading
                ? <Box as="span" fontSize={{ base: "14px", md: "18px" }}>⬇</Box>
                : null
            }
            w={{ base: "100%", sm: "auto" }}
            h={{ base: "32px", sm: "38px", md: "42px" }}
            px={{ base: 6, md: 9 }}
            fontSize={{ base: "xs", sm: "sm", md: "md" }}
            fontWeight="bold"
            boxShadow="0 2px 4px rgba(33, 199, 74, 0.3)"
            _hover={{
              transform: downloadLoading ? "none" : "translateY(-2px)",
              boxShadow: downloadLoading
                ? "0 2px 4px rgba(33, 199, 74, 0.3)"
                : "0 4px 8px rgba(33, 199, 74, 0.4)",
            }}
            transition="all 0.2s"
            isDisabled={downloadLoading || rowData.length === 0}
          >
            Download Excel
          </Button>
        </Flex>

        {error && (
          <Text color="red.500" fontWeight="bold" mb={3}>{error}</Text>
        )}
      </Box>

      {/* ── AG Grid ── */}
      <Box mb={{ base: 3, lg: 0 }}  >
        <AgGridWrapper
          rowData={rowData}
          columnDefs={columnDefs}
          loading={loading}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          height="500px"
          extraGridProps={{
            pagination: true,
            paginationPageSize: pageSize,
            suppressPaginationPanel: true,
            headerHeight: window.innerWidth < 768 ? 55 : 65,
            onPaginationChanged: (params) => {
              setCurrentPage(params.api.paginationGetCurrentPage() + 1);
            },
            animateRows: true,
          }}
          noRowsMessage="No records found for the selected date range"
        />
      </Box>

      {/* ── Custom Pagination ── */}
      <Flex
        justify="space-between"
        align="center"
        mt={4}
        px={{ base: 2, md: 4 }}
        flexDirection={{ base: "column", sm: "row" }}
        gap={{ base: 3, sm: 0 }}
      >
        <Text fontSize={{ base: "xs", sm: "sm" }} color="gray.600">
          Showing {filteredCount > 0 ? (currentPage - 1) * pageSize + 1 : 0} to{" "}
          {Math.min(currentPage * pageSize, filteredCount)} of {filteredCount} records
        </Text>

        <Flex gap={2} align="center">
          <Button
            onClick={handlePrevious}
            isDisabled={currentPage === 1}
            size="sm"
            fontSize={{ base: "md", md: "lg" }}
            variant="outline"
            colorScheme="gray"
            leftIcon={<ChevronLeftIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Previous
          </Button>

          <Flex
            align="center" justify="center"
            bg="red.500" color="white"
            w={{ base: "32px", md: "40px" }}
            h={{ base: "32px", md: "40px" }}
            borderRadius="md" fontWeight="bold"
            fontSize={{ base: "sm", md: "md" }}
            boxShadow="0 2px 4px rgba(199,33,39,0.3)"
          >
            {currentPage}
          </Flex>

          <Button
            onClick={handleNext}
            isDisabled={currentPage >= totalPages}
            size="sm"
            fontSize={{ base: "md", md: "lg" }}
            variant="outline"
            colorScheme="gray"
            rightIcon={<ChevronRightIcon />}
            boxShadow="0 1px 3px rgba(0,0,0,0.1)"
            _hover={{ boxShadow: "0 2px 6px rgba(0,0,0,0.15)" }}
            _disabled={{ opacity: 0.4, cursor: "not-allowed", boxShadow: "none" }}
          >
            Next
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
}